import type {
  SlideDetailAdapterParams,
  SlideDetailAdapterResult,
  SlideDetailAdapterResultKey,
} from '@/common/types/slideDetailAdapter.types';
import { createSlideDetailResult } from '@/common/utils/slideDetailAdapterResult';
import { useMessage } from '@/common/inject/useMessage';
import { useInternational } from '@/common/locale';
import { getSlideDetailStore, TOTAL_DETAIL } from '@/common/stores/slide-detail';
import { DetailTypeToPermissionKey } from '@/common/stores/slide-detail.logic';
import { ref } from 'vue';
import { getAlertTarget, getAlertType } from '../utils/utils';
import { RuleCriteria } from '../components/alertDetail/alertDetail.types';
import { AlertTargetItem } from '../components/alertTargetList/alertTargetList.types';
import { CHECK_BY_TARGET } from '../utils/define';
import type { RolePermissionKeyType } from '@/config/views/permission/rolePermission/rolePermission.type';
import { z } from 'zod';
import { getRequiredMap } from '@/common/utils/core/detailFieldMeta.utils';

// 연계 파라미터로 타겟이 문자열(alertTargetId 등)로 유입될 수 있어, 배열로 정규화해
// 스키마 출력 계약을 AlertTargetItem[]로 통일한다 (소비부가 문자열 케이스를 처리할 필요 없도록).
const normalizeAlertTargets = (
  value?: string | AlertTargetItem[],
): AlertTargetItem[] | undefined => (typeof value === 'string' ? [{ id: value, name: '' }] : value);

const alertRuleDetailInputSchema = z
  .object({
    targetId: z.string().optional(),
    alertRuleId: z.string().optional(),
    alertRuleType: z.string().optional(),
    alertRuleName: z.string().optional(),
    alertTargetId: z.string().optional(),
    targets: z.union([z.string(), z.custom<AlertTargetItem[]>()]).optional(),
    ruleCriteria: z.string().optional(),
    rawData: z.record(z.any()).optional(),
    fromTime: z.string().optional(),
    toTime: z.string().optional(),
  })
  .transform(({ targetId, alertRuleId, alertTargetId, targets, rawData, ...rest }) => ({
    ...rest,
    alertRuleId: alertRuleId || rawData?.alertRuleId || targetId,
    alertRuleName: rest.alertRuleName || rawData?.alertRuleName,
    alertRuleType: getAlertType(rest.alertRuleType || rawData?.alertRuleType || ''),
    targets: normalizeAlertTargets(
      targets ||
        alertTargetId ||
        (rawData?.ruleCriteria === CHECK_BY_TARGET ? rawData?.targetId : undefined),
    ),
    ruleCriteria: rest.ruleCriteria || rawData?.ruleCriteria,
  }))
  .pipe(
    z.object({
      alertRuleId: z.string().min(1),
      alertRuleType: z.string().min(1),
      alertRuleName: z.string().min(1),
      targets: z.custom<AlertTargetItem[]>().optional(),
      ruleCriteria: z.string().optional(),
      fromTime: z.string().optional(),
      toTime: z.string().optional(),
    }),
  );

export type AlertRuleDetailInput = z.input<typeof alertRuleDetailInputSchema>;

export const alertDetailRequiredMap = getRequiredMap(alertRuleDetailInputSchema);

export const useAlertRuleDetailAdapter = (rolePermissionKey?: RolePermissionKeyType) => {
  const isSlideDetailOpened = ref(false);

  const { showErrorMsg } = useMessage();
  const { t } = useInternational();
  const { addTabPanel, isDetailSlidePermissionDenied } = getSlideDetailStore();

  const validateAlertRuleDetail = (
    params: AlertRuleDetailInput,
    options: {
      showErrorMessage: boolean;
      checkPermission?: boolean;
      checkParams?: boolean;
    },
  ): {
    reason: SlideDetailAdapterResultKey;
    parsedParams?: z.infer<typeof alertRuleDetailInputSchema>;
  } => {
    const { checkPermission = true, checkParams = true } = options;

    // 1. 권한 체크
    if (checkPermission) {
      const denied = isDetailSlidePermissionDenied({
        rolePermissionKey: rolePermissionKey || DetailTypeToPermissionKey[TOTAL_DETAIL.ALERT],
        type: TOTAL_DETAIL.ALERT,
        showError: options.showErrorMessage,
      });
      if (denied) return { reason: 'permission_denied' };
    }

    const parsed = alertRuleDetailInputSchema.safeParse(params);

    if (checkParams && !parsed.success) {
      if (options.showErrorMessage) {
        const missingParams = parsed.error.issues.map((issue) => issue.path[0]).join(', ');
        showErrorMsg({
          msgStr: t('MESSAGE.LINK_MISSING_REQUIRED_PARAMS', { value: missingParams }),
        });
      }
      return { reason: 'invalid_params' };
    }
    if (!parsed.success) {
      return { reason: 'invalid_params' };
    }

    return { reason: 'ok', parsedParams: parsed.data };
  };

  const openAlertRuleDetail = async ({
    params,
    options,
    selectedTab,
    state,
    init,
    currentState,
  }: SlideDetailAdapterParams<AlertRuleDetailInput>): Promise<SlideDetailAdapterResult> => {
    const { runCheckPermission = true, showErrorMessage = true } = options ?? {};

    const { reason, parsedParams } = validateAlertRuleDetail(params, {
      showErrorMessage,
      checkPermission: runCheckPermission,
    });
    if (reason !== 'ok' || !parsedParams) return createSlideDetailResult(reason);

    const ruleType = getAlertType(parsedParams.alertRuleType);
    const { alertTargetId, alertTargetName } = getAlertTarget(
      parsedParams.ruleCriteria as RuleCriteria,
      parsedParams.targets,
      ruleType,
    );

    const result = addTabPanel(
      {
        type: TOTAL_DETAIL.ALERT,
        name: `${parsedParams.alertRuleName}${alertTargetName ? ` (${alertTargetName})` : ''}`,
        addition: {
          alertRuleId: parsedParams.alertRuleId || '',
          alertRuleName: parsedParams.alertRuleName,
          alertRuleType: ruleType,
          alertTargetId,
          initialSelectedTab: selectedTab, // 호출자가 지정한 초기 탭을 폴백으로 보존하기 위해 저장
          fromTime: parsedParams.fromTime,
          toTime: parsedParams.toTime,
        },
        state: { ...state, selectedTab },
        init,
      },
      currentState,
    );

    isSlideDetailOpened.value = getSlideDetailStore().isOpened;
    return createSlideDetailResult(result);
  };

  return {
    isSlideDetailOpened,
    openAlertRuleDetail,
  };
};
